import { Spacing } from '@persona-id/design-tokens';
import { FC, useCallback } from 'react';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import WorkflowModuleCallersList from 'dashboard/components/WorkflowModuleCallersList/WorkflowModuleCallersList';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  WorkflowModule,
  WorkflowModuleAttributes,
  WorkflowModuleStatus,
} from 'dashboard/models/workflow-module';
import {
  useActivateWorkflowModuleMutation,
  useArchiveWorkflowModuleMutation,
  useDeactivateWorkflowModuleMutation,
} from 'dashboard/redux/api/workflow-module';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';

interface WorkflowModuleModalsProps {
  moduleToActivate: WorkflowModule | null;
  moduleToArchive: WorkflowModule | null;
  moduleToDeactivate: WorkflowModule | null;
  setModuleToActivate: (module: WorkflowModule | null) => void;
  setModuleToArchive: (module: WorkflowModule | null) => void;
  setModuleToDeactivate: (module: WorkflowModule | null) => void;
}

const WorkflowModuleModals: FC<WorkflowModuleModalsProps> = ({
  moduleToActivate,
  moduleToArchive,
  moduleToDeactivate,
  setModuleToActivate,
  setModuleToArchive,
  setModuleToDeactivate,
}: WorkflowModuleModalsProps) => {
  const [activateModule, { error: activateModuleError }] = useActivateWorkflowModuleMutation();
  const closeActivateModal = useCallback(() => setModuleToActivate(null), [setModuleToActivate]);
  useRTKErrorHandler({
    error: activateModuleError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });
  const confirmActivateModule = useCallback(async () => {
    if (moduleToActivate) {
      await activateModule(moduleToActivate);
      closeActivateModal();
    }
  }, [moduleToActivate, activateModule, closeActivateModal]);

  const [archiveModule, { error: archiveModuleError }] = useArchiveWorkflowModuleMutation();
  const closeArchiveModal = useCallback(() => setModuleToArchive(null), [setModuleToArchive]);
  useRTKErrorHandler({
    error: archiveModuleError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });
  const confirmArchiveModule = useCallback(async () => {
    if (moduleToArchive) {
      await archiveModule(moduleToArchive);
      closeArchiveModal();
    }
  }, [moduleToArchive, archiveModule, closeArchiveModal]);

  const [deactivateModule, { error: deactivateModuleError }] =
    useDeactivateWorkflowModuleMutation();
  const closeDeactivateModal = useCallback(
    () => setModuleToDeactivate(null),
    [setModuleToDeactivate],
  );
  useRTKErrorHandler({
    error: deactivateModuleError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });
  const confirmDeactivateModule = useCallback(async () => {
    if (moduleToDeactivate) {
      await deactivateModule(moduleToDeactivate);
      closeDeactivateModal();
    }
  }, [moduleToDeactivate, deactivateModule, closeDeactivateModal]);

  const moduleToArchiveOrDeactivate = moduleToArchive || moduleToDeactivate;
  const callers = moduleToArchiveOrDeactivate?.attributes?.callers || [];
  const referencedByWorkflows = Boolean(callers.length);

  return (
    <>
      <ModalWrapper isOpen={moduleToActivate != null} handleCloseModal={closeActivateModal}>
        <ConfirmationModal
          cancelAction={closeActivateModal}
          confirmAction={confirmActivateModule}
          header='Activate this module?'
          confirmActionText='Activate'
        >
          <Paragraph>This will enable usage of this module in all environments.</Paragraph>
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={moduleToArchive != null} handleCloseModal={closeArchiveModal}>
        <ConfirmationModal
          cancelAction={closeArchiveModal}
          confirmAction={confirmArchiveModule}
          header='Archive this module?'
          confirmActionText='Archive'
          allowConfirm={
            !referencedByWorkflows ||
            moduleToArchive?.attributes.status === WorkflowModuleStatus.Inactive
          }
          size={referencedByWorkflows ? 'lg' : 'md'}
        >
          {referencedByWorkflows ? (
            <ReferencedWorkflowsModalContent callers={callers} modalAction='archive' />
          ) : (
            <Paragraph>
              <strong>This action is permanent and can’t be undone.</strong> Please ensure that this
              module is not in use before continuing.
            </Paragraph>
          )}
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={moduleToDeactivate != null} handleCloseModal={closeDeactivateModal}>
        <ConfirmationModal
          cancelAction={closeDeactivateModal}
          confirmAction={confirmDeactivateModule}
          header='Deactivate this module?'
          confirmActionText='Deactivate'
          size={referencedByWorkflows ? 'lg' : 'md'}
        >
          {referencedByWorkflows ? (
            <ReferencedWorkflowsModalContent callers={callers} modalAction='deactivate' />
          ) : (
            <Paragraph>
              <strong>This will disable usage of this module in all environments.</strong> Please
              ensure that this module is not in use before continuing.
            </Paragraph>
          )}
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

interface ReferencedWorkflowModalContentProps {
  callers: WorkflowModuleAttributes['callers'];
  modalAction: 'archive' | 'deactivate';
}

const ReferencedWorkflowModalContentContainer = styled('div')`
  display: flex;
  align-items: stretch;
  flex-direction: column;
  justify-content: space-between;
  gap: ${Spacing[2]};
`;

export const ReferencedWorkflowsModalContent: FC<ReferencedWorkflowModalContentProps> = ({
  callers,
  modalAction,
}) => {
  return (
    <ReferencedWorkflowModalContentContainer>
      <Paragraph>
        This module is currently being used in {callers.length} workflow(s). To {modalAction} this
        module, begin by removing the modules from the workflow(s) listed below.
      </Paragraph>
      <Spacer height={1} />
      <WorkflowModuleCallersList callers={callers} />
    </ReferencedWorkflowModalContentContainer>
  );
};

export default WorkflowModuleModals;
